import {
  type RowKey,
  useBoardCollapsedRows,
  useBoardElementRef,
  useBoardLabels,
  useBoardRow,
} from '@github-ui/board-core'
import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {type ReactNode, type Ref, useLayoutEffect, useRef} from 'react'

import styles from './BoardRowFrame.module.css'

export type BoardRowFrameProps = {
  rowKey: RowKey
  /**
   * The row's header. Typically a `BoardRowHeader`.
   *
   * When it is absent the row draws no chrome and claims no accessible name, so a board
   * with no swimlane reads exactly as it did before swimlanes existed. That is a
   * decision about whether the caller asked for row headers, not about how many rows
   * the board has. A genuinely swimlaned board whose second row has not paged in yet
   * keeps its headers.
   */
  header?: ReactNode
  /** The row's strip of cells. */
  children?: ReactNode
  className?: string
  virtualIndex?: number
  ref?: Ref<HTMLDivElement>
}

/**
 * One swimlane: its header, and the strip of cells beneath it.
 *
 * A board with no swimlane has exactly one row, so this renders for every board and
 * nothing anywhere asks whether the board is swimlaned.
 */
export function BoardRowFrame({rowKey, header, children, className, virtualIndex, ref}: BoardRowFrameProps) {
  const {row, rowProps} = useBoardRow(rowKey)
  const {collapsedRows, setRowCollapsed} = useBoardCollapsedRows()
  const boardElementRef = useBoardElementRef()
  const labels = useBoardLabels()
  const headerElementRef = useRef<HTMLDivElement>(null)
  const pendingHeaderTopRef = useRef<number | null>(null)
  // `== null` rather than `=== undefined`, so a caller whose `renderRowHeader` returns
  // `null` for one row does not end up with some swimlanes named and others not.
  const hasHeader = header != null
  const framing = hasHeader ? rowProps : {}
  const canCollapse = hasHeader && row !== undefined
  const isCollapsed = canCollapse && collapsedRows.has(rowKey)

  useLayoutEffect(() => {
    const previousTop = pendingHeaderTopRef.current
    pendingHeaderTopRef.current = null
    const board = boardElementRef.current
    const headerElement = headerElementRef.current
    if (previousTop === null || board === null || headerElement === null) return

    board.scrollTop += headerElement.getBoundingClientRect().top - previousTop
  }, [boardElementRef, isCollapsed])

  return (
    <div
      {...framing}
      ref={ref}
      data-index={virtualIndex}
      data-collapsed={isCollapsed ? '' : undefined}
      className={clsx(styles.RowFrame, className)}
    >
      {hasHeader ? (
        <div ref={headerElementRef} className={styles.Header}>
          <div className={styles.HeaderContent}>
            {canCollapse ? (
              <IconButton
                className={styles.CollapseButton}
                icon={isCollapsed ? ChevronRightIcon : ChevronDownIcon}
                aria-label={isCollapsed ? labels.expandRow(row.label) : labels.collapseRow(row.label)}
                aria-expanded={!isCollapsed}
                size="small"
                variant="invisible"
                onClick={() => {
                  pendingHeaderTopRef.current = headerElementRef.current?.getBoundingClientRect().top ?? null
                  setRowCollapsed(rowKey, !isCollapsed)
                }}
              />
            ) : null}
            {header}
          </div>
        </div>
      ) : null}
      <div hidden={isCollapsed} className={styles.Cells}>
        {isCollapsed ? null : children}
      </div>
    </div>
  )
}
